// StepReportModal — "Add Report" / "Edit Report" pop-up for a single Order Step.
// Writes to PUT /orders/:orderId/step-reports/:step.
// Body: { note: string | null, paths: string[] } (up to 10 Sharepoint paths).
// The server replaces paths wholesale; the client owns the full list on every save.

import { useState } from 'react';
import { Modal, Textarea, Button, Group, Text, Table, TextInput, ActionIcon, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api } from '../../api/client';

interface Props {
  orderId: number;
  step: string;
  existingNote: string | null;
  existingPaths: string[];
  onClose: () => void;
  onSaved: (updatedOrder: any) => void;
}

const MAX_CHARS = 500;
const MAX_PATHS = 10;

export default function StepReportModal({
  orderId, step, existingNote, existingPaths, onClose, onSaved,
}: Props) {
  const [note, setNote] = useState(existingNote ?? '');
  // Keep paths as an array of strings; empty string = blank row being edited.
  const [paths, setPaths] = useState<string[]>(existingPaths.length > 0 ? existingPaths : ['']);
  const [saving, setSaving] = useState(false);

  const isEdit = !!(existingNote || existingPaths.length > 0);
  const remaining = MAX_CHARS - note.length;

  function updatePath(idx: number, value: string) {
    setPaths((prev) => prev.map((p, i) => (i === idx ? value : p)));
  }

  function removePath(idx: number) {
    setPaths((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [''];
    });
  }

  function addPath() {
    if (paths.length < MAX_PATHS) setPaths((prev) => [...prev, '']);
  }

  async function save() {
    // Strip blank rows before persisting.
    const cleanPaths = paths.map((p) => p.trim()).filter((p) => p.length > 0);
    setSaving(true);
    try {
      const o = await api.put<any>(
        `/orders/${orderId}/step-reports/${encodeURIComponent(step)}`,
        { note: note.trim() || null, paths: cleanPaths },
      );
      onSaved(o);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    setSaving(true);
    try {
      const o = await api.put<any>(
        `/orders/${orderId}/step-reports/${encodeURIComponent(step)}`,
        { note: null, paths: [] },
      );
      onSaved(o);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Clear failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={<span style={{ fontWeight: 700 }}>{isEdit ? 'Edit Report' : 'Add Report'} — {step}</span>}
      size="lg"
    >
      <Stack gap="md">
        {/* Note section */}
        <div>
          <Text size="sm" fw={500} mb={4}>Report note</Text>
          <Textarea
            data-tour="report-text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value.slice(0, MAX_CHARS))}
            placeholder="Describe what happened during this step…"
            minRows={4}
            autosize
            maxRows={10}
          />
          <Text size="xs" c={remaining < 50 ? 'red' : 'dimmed'} ta="right" mt={4}>
            {remaining} characters remaining
          </Text>
        </div>

        {/* Sharepoint paths table */}
        <div>
          <Text size="sm" fw={500} mb={6}>Sharepoint document paths</Text>
          <Table withRowBorders={false} verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>#</Table.Th>
                <Table.Th>Path</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paths.map((p, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{idx + 1}</Text>
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      data-tour="report-path"
                      value={p}
                      onChange={(e) => updatePath(idx, e.currentTarget.value)}
                      placeholder="https://company.sharepoint.com/…"
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="subtle"
                      onClick={() => removePath(idx)}
                      disabled={paths.length === 1 && p === ''}
                      title="Remove path"
                    >
                      ✕
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {paths.length < MAX_PATHS && (
            <Button
              size="compact-xs"
              variant="light"
              mt={6}
              onClick={addPath}
            >
              + Add path
            </Button>
          )}
          {paths.length >= MAX_PATHS && (
            <Text size="xs" c="dimmed" mt={4}>Maximum of {MAX_PATHS} paths reached.</Text>
          )}
        </div>
      </Stack>

      <Group justify="space-between" mt="xl">
        {isEdit && (
          <Button variant="subtle" color="red" size="compact-sm" onClick={clear} loading={saving}>
            Clear report
          </Button>
        )}
        <Group ml="auto" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-tour="report-save" onClick={save} loading={saving}>Save</Button>
        </Group>
      </Group>
    </Modal>
  );
}
